// The market's live feed: one Action Cable subscription per edition (and one more for
// the leaderboard page), shared by every component on the page, folding each message
// into SWR's cache so the surfaces re-render without fetching anything.
//
// The JSON routes are still the source of truth. They are read on page load, again on
// every reconnect (the socket may have missed messages), and polled only while the
// socket is down — `useMarketFeed` hands each hook the interval to use.
import { type Consumer, createConsumer } from '@rails/actioncable';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import useSWR, { mutate } from 'swr';
import { useEventSlug } from '@/hooks/use-event-slug';
import type {
  LeaderboardResponse,
  PortfolioResponse,
  StateResponse,
} from '@/types/market';
import {
  applyLeaderboard,
  applyState,
  applyTrade,
  hasUnknownProjects,
  type LeaderboardMessage,
  type MarketMessage,
  revaluePortfolio,
  type TradeMessage,
} from './live-state';

export const stateKey = (slug: string) => `/api/market/state?event=${slug}`;
export const portfolioKey = (slug: string) =>
  `/api/market/portfolio?event=${slug}`;
export const leaderboardKey = (slug: string) =>
  `/api/market/leaderboard?event=${slug}`;
export const historyKey = (slug: string, projectId: string) =>
  `/api/market/history/${projectId}?event=${slug}`;

const fetchJson = (url: string) => fetch(url).then((r) => r.json());

type Channel = 'MarketChannel' | 'MarketLeaderboardChannel';

type Feed = {
  refs: number;
  connected: boolean;
  everConnected: boolean;
  unsubscribe: () => void;
};

let consumer: Consumer | null = null;
const feeds = new Map<string, Feed>();
const listeners = new Set<() => void>();
const tradeListeners = new Map<string, Set<(trade: TradeMessage) => void>>();
// The newest trade each edition has pushed, laid over any fetch that answers with an
// older share vector (a request in flight while the trade landed).
const lastTrade = new Map<string, TradeMessage>();

const notify = () => {
  for (const listener of listeners) listener();
};

const feedKey = (channel: Channel, slug: string) => `${channel}:${slug}`;

// Everything the market keeps under this edition, for a resync after a reconnect.
const isMarketKey = (slug: string) => (key: unknown) =>
  typeof key === 'string' &&
  key.startsWith('/api/market/') &&
  key.endsWith(`event=${slug}`);

function onMarketMessage(slug: string, message: MarketMessage) {
  if (message.type === 'trade') {
    const previous = lastTrade.get(slug);
    if (!previous || previous.version < message.version)
      lastTrade.set(slug, message);
    let refetch = false;
    void mutate<StateResponse>(
      stateKey(slug),
      (state) => {
        if (!state) return state;
        refetch = hasUnknownProjects(state, message);
        return applyTrade(state, message);
      },
      { revalidate: false },
    ).then(() => {
      if (refetch) void mutate(stateKey(slug));
    });
    for (const listener of tradeListeners.get(slug) ?? []) listener(message);
  } else if (message.type === 'state') {
    void mutate<StateResponse>(
      stateKey(slug),
      (state) => state && applyState(state, message.state),
      { revalidate: false },
    );
  }
}

function onLeaderboardMessage(slug: string, message: LeaderboardMessage) {
  void mutate<LeaderboardResponse>(
    leaderboardKey(slug),
    (board) => board && applyLeaderboard(board, message),
    { revalidate: false },
  );
}

function subscribe(channel: Channel, slug: string): Feed {
  consumer ??= createConsumer();
  const key = feedKey(channel, slug);
  const feed: Feed = {
    refs: 0,
    connected: false,
    everConnected: false,
    unsubscribe: () => {},
  };
  const subscription = consumer.subscriptions.create(
    { channel, event: slug },
    {
      connected() {
        // The first connect only has the server-rendered state to catch up on; a
        // reconnect may have missed anything, so everything is read again.
        if (channel === 'MarketChannel')
          void mutate(feed.everConnected ? isMarketKey(slug) : stateKey(slug));
        else if (feed.everConnected) void mutate(leaderboardKey(slug));
        feed.connected = true;
        feed.everConnected = true;
        notify();
      },
      disconnected() {
        feed.connected = false;
        notify();
      },
      rejected() {
        feed.connected = false;
        notify();
      },
      received(message: MarketMessage | LeaderboardMessage) {
        if (message.type === 'leaderboard') onLeaderboardMessage(slug, message);
        else onMarketMessage(slug, message);
      },
    },
  );
  feed.unsubscribe = () => {
    subscription.unsubscribe();
    feeds.delete(key);
  };
  feeds.set(key, feed);
  return feed;
}

function retain(channel: Channel, slug: string) {
  const feed =
    feeds.get(feedKey(channel, slug)) ?? subscribe(channel, slug);
  feed.refs += 1;
  return () => {
    feed.refs -= 1;
    if (feed.refs === 0) feed.unsubscribe();
  };
}

const subscribeStore = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Keeps the edition's feed open while the calling component is mounted, and answers
// whether it is live — the hooks below poll only when it is not.
export function useMarketFeed(
  slug: string,
  channel: Channel = 'MarketChannel',
): boolean {
  useEffect(() => retain(channel, slug), [channel, slug]);
  return useSyncExternalStore(
    subscribeStore,
    () => feeds.get(feedKey(channel, slug))?.connected ?? false,
    () => false,
  );
}

export function useMarketTrades(
  slug: string,
  listener: (trade: TradeMessage) => void,
) {
  useEffect(() => {
    const set = tradeListeners.get(slug) ?? new Set();
    tradeListeners.set(slug, set);
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }, [slug, listener]);
}

// The edition's `StateResponse`, seeded from the server-rendered props (no fetch on
// mount) and kept current by the feed.
export function useMarketState(initial: StateResponse): StateResponse {
  const slug = useEventSlug();
  const live = useMarketFeed(slug);
  const { data, mutate: mutateState } = useSWR<StateResponse>(
    stateKey(slug),
    async (url: string) => {
      const fresh: StateResponse = await fetchJson(url);
      const trade = lastTrade.get(slug);
      return trade ? applyTrade(fresh, trade) : fresh;
    },
    {
      fallbackData: initial,
      revalidateOnMount: false,
      refreshInterval: live ? 0 : 5000,
      refreshWhenHidden: false,
    },
  );
  // Inertia navigations reuse the cache: keep whichever snapshot is newer.
  useEffect(() => {
    void mutateState(
      (cached) => (!cached || cached.version < initial.version ? initial : cached),
      { revalidate: false },
    );
  }, [mutateState, initial]);
  return data ?? initial;
}

// The viewer's portfolio, revalued at the live prices on every trade anyone makes.
// Undefined until it has loaded, and for anyone who cannot trade.
export function useLivePortfolio(
  state: StateResponse,
  enabled: boolean,
): {
  portfolio: PortfolioResponse | { error: string } | undefined;
  error: unknown;
  refresh: () => Promise<unknown>;
} {
  const slug = useEventSlug();
  const live = useMarketFeed(slug);
  const { data, error, mutate: refresh } = useSWR<
    PortfolioResponse | { error: string }
  >(enabled ? portfolioKey(slug) : null, fetchJson, {
    refreshInterval: live ? 0 : 10000,
    refreshWhenHidden: false,
  });
  const portfolio = useMemo(
    () => (data && !('error' in data) ? revaluePortfolio(data, state) : data),
    [data, state],
  );
  return { portfolio, error, refresh };
}

export function useLiveLeaderboard(): LeaderboardResponse | undefined {
  const slug = useEventSlug();
  const live = useMarketFeed(slug, 'MarketLeaderboardChannel');
  const { data } = useSWR<LeaderboardResponse>(leaderboardKey(slug), fetchJson, {
    refreshInterval: live ? 0 : 15000,
    refreshWhenHidden: false,
  });
  return data;
}
